/**
 * TD - Floating widget lift service.
 *
 * Third-party floating widgets (cookie-consent and accessibility toggles, promo
 * teasers) pin themselves to the bottom of the viewport, where they collide with any
 * bar we pin there ourselves. This service lifts them clear of that bar and settles
 * them back when it goes away.
 *
 * Any number of bars can claim bottom space by id; the tallest live claim wins, so a
 * page with more than one pinned bar never double-lifts. Widgets render their buttons
 * inside open shadow roots we don't own, so the shift is applied in JS (CSS can't
 * reach in) and takes its transition from the claiming bar so everything moves in
 * lockstep.
 *
 * Consumers: assets/td-sticky-atc.js (PDP), assets/td-bundle-builder.js (build a
 * bundle cart drawer).
 *
 * Usage:
 *   TDFloatingWidgets.claim('sticky-atc', barHeightPx, { element: barEl });
 *   TDFloatingWidgets.release('sticky-atc');
 */
(function () {
  if (window.TDFloatingWidgets) return;

  // Gap left between the bar and the first lifted widget, and between stacked widgets.
  var GAP = 8;

  // A widget's bottom-anchored fixed element is a lift target; a top/centre-anchored
  // panel (e.g. an open modal) is not. This is the max `bottom` (px) we treat as
  // "pinned to the bottom".
  var BOTTOM_ANCHOR_MAX_PX = 400;

  // Floating widgets to lift. Each entry is a selector matching either a custom
  // element whose open shadow root holds the fixed button (csm-*), or a light-DOM
  // element that is itself the fixed widget (omnisend teaser). Override site-wide via
  // window.TD_STICKY_ATC_WIDGET_SELECTORS.
  var DEFAULT_WIDGET_SELECTORS = [
    'csm-web-accessibility',
    'csm-cookie-consent',
    '[class*="omnisend-form-"][class*="-teaser"]',
  ];

  // Reserved height published for CSS that needs to sit clear of the bar too.
  var OFFSET_VAR = '--td-floating-offset';

  var claims = {};
  var shifted = [];
  var cachedTargets = null;
  var watcher = null;
  var watchPending = false;
  var watchFrame = null;

  function widgetSelectors() {
    var list = window.TD_STICKY_ATC_WIDGET_SELECTORS || DEFAULT_WIDGET_SELECTORS;
    return Array.isArray(list) ? list : DEFAULT_WIDGET_SELECTORS;
  }

  function bottomAnchorMax() {
    var configured = Number(window.TD_STICKY_ATC_BOTTOM_ANCHOR_MAX);
    return isFinite(configured) && configured >= 0 ? configured : BOTTOM_ANCHOR_MAX_PX;
  }

  // Settle a widget back to rest and clear cached geometry so the next lift re-measures.
  function resetWidget(node) {
    node.style.transform = node.tdStickyOriginalTransform || '';
    node.style.transition = node.tdStickyOriginalTransition || '';
    delete node.tdStickyOriginalTransform;
    delete node.tdStickyOriginalTransition;
    delete node.tdStickyBaseTransform;
    delete node.tdStickyOffset;
    delete node.tdStickyNaturalGap;
  }

  // Tallest live claim; 0 when nothing is claiming.
  function activeHeight() {
    var height = 0;
    for (var id in claims) {
      if (claims[id] && claims[id].height > height) height = claims[id].height;
    }
    return height;
  }

  // Read a claim's transition once when it is published instead of forcing a style
  // calculation on every late-widget mutation.
  function transitionForElement(element) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'none';
    if (!element) return 'transform 0.2s ease';

    var cs = getComputedStyle(element);
    var duration = (cs.transitionDuration || '0.2s').split(',')[0].trim();
    var easing = (cs.transitionTimingFunction || 'ease').split(',')[0].trim();
    return duration === '0s' ? 'none' : 'transform ' + duration + ' ' + easing;
  }

  // The claim that set the winning height decides the transition, so widgets move in
  // lockstep with whichever bar is actually on screen.
  function activeTransition() {
    var height = activeHeight();
    for (var id in claims) {
      if (claims[id] && claims[id].height === height) {
        return claims[id].transition;
      }
    }
    return 'transform 0.2s ease';
  }

  // Bottom-anchored floating elements for each configured widget. Custom elements
  // (csm-*) keep their button inside an open shadow root; light-DOM widgets (omnisend
  // teaser) are the element themselves. Accepts fixed OR absolute — the omnisend
  // teaser is an absolute box anchored to a zero-size fixed wrapper — then keeps only
  // the outermost match per widget so we don't double-move nested parts.
  function widgetTargets() {
    if (cachedTargets) {
      cachedTargets = cachedTargets.filter(function (node) { return node.isConnected; });
      return cachedTargets;
    }

    var candidates = [];
    var anchorMax = bottomAnchorMax();
    widgetSelectors().forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (host) {
        var nodes = host.shadowRoot
          ? Array.prototype.slice.call(host.shadowRoot.querySelectorAll('*'))
          : [host];
        nodes.forEach(function (node) {
          // Skip collapsed/zero-size wrappers (a real button has both dimensions);
          // some are also app-locked against transforms.
          if (!node.offsetWidth || !node.offsetHeight) return;
          var cs = getComputedStyle(node);
          if (cs.position !== 'fixed' && cs.position !== 'absolute') return;
          var bottom = parseFloat(cs.bottom);
          if (!isFinite(bottom) || bottom > anchorMax) return;
          if (candidates.indexOf(node) === -1) candidates.push(node);
        });
      });
    });
    cachedTargets = candidates.filter(function (node) {
      return !candidates.some(function (other) {
        return other !== node && other.contains(node);
      });
    });
    return cachedTargets;
  }

  // barHeight > 0 lifts widgets clear of the bar; 0 settles them back. Widgets that
  // share a screen corner are stacked (not piled on the same spot) so a crowded
  // corner — e.g. a consent toggle plus a promo teaser — doesn't overlap itself.
  function shiftWidgets(barHeight) {
    document.documentElement.style.setProperty(OFFSET_VAR, Math.max(0, barHeight) + 'px');

    if (barHeight <= 0) {
      shifted.forEach(resetWidget);
      shifted = [];
      cachedTargets = null;
      return;
    }

    var transition = activeTransition();
    var viewportH = window.innerHeight;
    var viewportW = window.innerWidth;
    var targets = widgetTargets();

    var items = targets.map(function (node) {
      // Capture the resting transform once so we compose rather than clobber it
      // (some widgets use translateX for centring). The computed-style read is a
      // once-per-target cost; resetWidget clears it before a future lift.
      if (node.tdStickyBaseTransform === undefined) {
        var base = getComputedStyle(node).transform;
        node.tdStickyOriginalTransform = node.style.transform;
        node.tdStickyOriginalTransition = node.style.transition;
        node.tdStickyBaseTransform = base && base !== 'none' ? base : '';
      }
      if (node.tdStickyOffset === undefined) node.tdStickyOffset = 0;
      var rect = node.getBoundingClientRect();
      // Cache the natural (un-shifted) gap so repeated re-shifts don't read a
      // mid-transition position and drift upward.
      if (node.tdStickyNaturalGap === undefined) {
        node.tdStickyNaturalGap = viewportH - rect.bottom - node.tdStickyOffset;
      }
      return {
        node: node,
        height: rect.height,
        naturalGap: node.tdStickyNaturalGap,
        isLeft: rect.left + rect.width / 2 < viewportW / 2,
      };
    });

    // Stack bottom-most first within each corner, starting just above the bar.
    items.sort(function (a, b) { return a.naturalGap - b.naturalGap; });
    var corners = {};
    items.forEach(function (item) {
      var key = item.isLeft ? 'left' : 'right';
      var floor = corners[key] === undefined ? barHeight + GAP : corners[key];
      var offset = Math.max(0, floor - item.naturalGap);
      var placedGap = item.naturalGap + offset;
      corners[key] = placedGap + item.height + GAP;

      item.node.tdStickyOffset = offset;
      item.node.style.transition = transition;
      item.node.style.transform =
        ('translateY(-' + Math.round(offset) + 'px) ' + item.node.tdStickyBaseTransform).trim();
    });

    // Settle back anything we lifted before that's no longer a target.
    shifted.forEach(function (node) {
      if (targets.indexOf(node) === -1) resetWidget(node);
    });
    shifted = targets;
  }

  // Re-shift on DOM changes while a claim is live (debounced to one frame), so widgets
  // that load in late get lifted without waiting for the next scroll.
  function elementTouchesWidget(node, includeDescendants) {
    if (node.nodeType !== 1) return false;
    return widgetSelectors().some(function (selector) {
      return node.matches(selector) ||
        node.closest(selector) ||
        (includeDescendants && node.querySelector(selector));
    });
  }

  function mutationTouchesWidget(mutation) {
    if (elementTouchesWidget(mutation.target, false)) return true;

    var changedNodes = []
      .concat(Array.prototype.slice.call(mutation.addedNodes))
      .concat(Array.prototype.slice.call(mutation.removedNodes));
    return changedNodes.some(function (node) { return elementTouchesWidget(node, true); });
  }

  function startWatch() {
    if (watcher) return;
    watcher = new MutationObserver(function (mutations) {
      if (!mutations.some(mutationTouchesWidget)) return;
      cachedTargets = null;
      if (watchPending) return;
      watchPending = true;
      watchFrame = requestAnimationFrame(function () {
        watchPending = false;
        watchFrame = null;
        var height = activeHeight();
        if (height > 0) shiftWidgets(height);
      });
    });
    watcher.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });
  }

  function stopWatch() {
    if (watcher) {
      watcher.disconnect();
      watcher = null;
    }
    if (watchFrame !== null) cancelAnimationFrame(watchFrame);
    watchFrame = null;
    watchPending = false;
  }

  function apply() {
    var height = activeHeight();
    shiftWidgets(height);
    if (height > 0) startWatch();
    else stopWatch();
  }

  window.TDFloatingWidgets = {
    /**
     * Reserve `height` px of bottom space for `id`. A height of 0 (or less) is the
     * same as releasing the claim. `options.element` is the bar itself, read for its
     * transition so lifted widgets move with it.
     */
    claim: function (id, height, options) {
      var px = Math.max(0, Math.ceil(height || 0));
      if (px <= 0) {
        delete claims[id];
      } else {
        var element = (options && options.element) || null;
        claims[id] = {
          height: px,
          transition: transitionForElement(element),
        };
      }
      apply();
    },

    release: function (id) {
      delete claims[id];
      apply();
    },

    /** Re-measure and re-apply — call after a layout change (resize, drawer toggle). */
    refresh: apply,
  };
})();
